/**
 * `ToastHost`: draws v2's toast (`@rista/core/toast`), mounted by each app's shell at its root. One body
 * for both apps, no platform split (the user, 2026-10-05), in web v1's react-toastify layout,
 * bottom-centre (`ReactToastify.css` 11.1). At 480 or narrower it spans the width, as react-toastify
 * does. Its colours are roles since DS1 (`look.ts`, QD8): the kind's status solid, not the dark theme.
 *
 * THE NEWEST MOUNTED HOST DRAWS. A React Native `Modal` (a sheet in presentation "modal") covers the
 * root, so a toast raised from inside it would be hidden under it. A sheet mounts its own `<ToastHost />`
 * inside its Modal: it is then the last registered and draws, the root host stays silent meanwhile, and
 * when the sheet unmounts the root draws again. Exactly one host draws at a time, so no toast shows twice.
 *
 * Paint is on inner Views, never the Pressable (CLAUDE.md, host CSS). The whole toast closes on a
 * press (v1's `closeOnClick`), so the ✕ is a picture, not a second control.
 */
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Pressable, Text, View, useWindowDimensions } from "react-native";

import { toasts, type ToastKind } from "@rista/core/toast";
import { Icon } from "@rista/ui/library/components/Icon";
import type { VectorIconName } from "@rista/ui/library/icons/vectors";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import { lookFor } from "@rista/ui/library/theme/variants";
import { TOAST_TONE, toastLook } from "./look";

/** react-toastify's compact breakpoint */
const COMPACT_WIDTH = 480;

/** react-toastify's icon per kind, in the icon registry */
const KIND_ICON: Record<ToastKind, VectorIconName> = { info: "toastInfo", success: "toastSuccess", error: "toastError" };

/** the mounted hosts, oldest first; the last one draws */
const mounted: number[] = [];
const watchers = new Set<() => void>();
let hostSeq = 0;
const newestHost = () => mounted[mounted.length - 1];
const watchHosts = (listener: () => void) => {
  watchers.add(listener);
  return () => void watchers.delete(listener);
};
function registerHost(id: number): () => void {
  mounted.push(id);
  watchers.forEach((w) => w());
  return () => {
    mounted.splice(mounted.indexOf(id), 1);
    watchers.forEach((w) => w());
  };
}

/** whether this host is the newest mounted one; false until it has registered, so a host never draws before it is counted */
function useDrawsToast(): boolean {
  const [id] = useState(() => ++hostSeq);
  useEffect(() => registerHost(id), [id]);
  return useSyncExternalStore(watchHosts, newestHost, newestHost) === id;
}

export function ToastHost() {
  const draws = useDrawsToast();
  const toast = useSyncExternalStore(toasts.subscribe, toasts.current, toasts.current);
  const compact = useWindowDimensions().width <= COMPACT_WIDTH;
  const theme = useTheme();
  if (!toast || !draws) return null;
  const look = lookFor(toastLook, theme, { kind: toast.kind, compact });
  // icons are pictures: their fill is the words' role, read off the theme rather than a resolved style
  const ink = theme.color.status[TOAST_TONE[toast.kind]].onSolid;
  return (
    <View pointerEvents="box-none" style={look.container}>
      <Pressable
        key={toast.id}
        testID="toast"
        role="alert"
        accessibilityLiveRegion="polite"
        onPress={toasts.dismiss}
        style={look.press}
      >
        <View style={look.toast}>
          <View style={look.icon}>
            <Icon name={KIND_ICON[toast.kind]} size={22} color={ink} />
          </View>
          <Text style={look.message}>{toast.message}</Text>
          <View style={look.close}>
            <Icon name="toastClose" size={16} color={ink} />
          </View>
        </View>
      </Pressable>
    </View>
  );
}
